import React, { useMemo, useState } from 'react';
import {
	Button,
	Tooltip,
	Drawer,
	DrawerContent,
	DrawerHeader,
	DrawerBody,
	DrawerFooter
} from '@heroui/react';
import { Filter, Maximize2, Minimize2 } from 'lucide-react';
import FilterTimezoneSelect from './FilterTimezoneSelect.js';
import FilterToggles from './FilterToggles.js';
import FilterMultiSelect from './FilterMultiSelect.js';
import SavedFiltersDropdown from './SavedFiltersDropdown.js';
import useBreakpoint from '../hooks/useBreakpoint.js';

const DEFAULT_DATA = Object.freeze({
	organizations: [],
	locations: [],
	resources: [],
	pilots: [],
	instructors: [],
	timezones: []
});

/**
 * Natural, case-insensitive alphanumeric sort.
 *
 * Matches the legacy Angular calendar's ordering: numbers come before
 * letters and sort by magnitude (`1, 2, 10` not `1, 10, 2`); letters
 * compare case-insensitively. Applied once at the UI layer so Month/
 * Week/Day/Dispatch all inherit the same order without per-adapter work.
 *
 * @param {Array} items
 * @param {(item: Object) => string} [keyFn]  Defaults to `item.name`.
 * @returns {Array}
 */
function sortItemsNatural(items, keyFn) {
	const getKey = typeof keyFn === 'function' ? keyFn : item => item && item.name;
	return (items || []).slice().sort((a, b) => {
		const an = String(getKey(a) ?? '');
		const bn = String(getKey(b) ?? '');
		return an.localeCompare(bn, undefined, { numeric: true, sensitivity: 'base' });
	});
}

/**
 * Build the flat `items` array the `FilterMultiSelect` org slot expects.
 * Each org entry becomes `{ id: uniqueSlug, label: name }` — id is the
 * unique slug so the slice's `filters.org` array can round-trip cleanly
 * through `toApiFilter`/`fromApiFilter` without needing a separate numeric
 * id lookup at the UI layer.
 */
function normalizeOrgOptions(organizations) {
	return (organizations || [])
		.map(org => {
			if (!org) return null;
			const id = org.unique != null ? org.unique : org.id;
			if (id == null) return null;
			return { id: String(id), label: org.name || String(id) };
		})
		.filter(Boolean);
}

/**
 * Bucket `items` into `{ key, title, items }` groups for `FilterMultiSelect`.
 *
 * Accepts two resolver callbacks so callers can compose multi-field keys and
 * multi-field titles (see sub-epic 10.3: resource/pilot/instructor dropdowns
 * show `locationName [organizationName]` headers). A missing key falls back
 * to `__ungrouped__`; a missing title falls back to `Other`.
 *
 * @param {Array} items
 * @param {(item: Object) => string|null|undefined} groupKeyFn
 * @param {(item: Object) => string|null|undefined} groupTitleFn
 * @returns {Array<{key: string, title: string, items: Array}>}
 */
function buildGroups(items, groupKeyFn, groupTitleFn, groupSubtitleFn) {
	const map = {};
	const order = [];
	(items || []).forEach(item => {
		if (!item || item.id == null) return;
		const rawKey = typeof groupKeyFn === 'function' ? groupKeyFn(item) : null;
		const mapKey = rawKey != null && rawKey !== '' ? String(rawKey) : '__ungrouped__';
		if (!map[mapKey]) {
			const rawTitle = typeof groupTitleFn === 'function' ? groupTitleFn(item) : null;
			const rawSubtitle = typeof groupSubtitleFn === 'function' ? groupSubtitleFn(item) : null;
			map[mapKey] = {
				key: mapKey,
				title: rawTitle != null && rawTitle !== '' ? String(rawTitle) : 'Other',
				subtitle: rawSubtitle != null && rawSubtitle !== '' ? String(rawSubtitle) : null,
				items: []
			};
			order.push(mapKey);
		}
		const labelParts = [item.name, item.surname].filter(Boolean);
		const label = labelParts.length ? labelParts.join(' ') : String(item.id);
		map[mapKey].items.push({ id: String(item.id), label });
	});
	return order.map(k => map[k]);
}

/**
 * Fully controlled, config-driven calendar filter bar. No Redux, no API calls.
 * Parent view owns filter state and passes handlers. Designed to be mounted
 * from Month / Week / Day views, each supplying its own `fields` subset.
 */
function CalendarFilterBar({
	fields = [],
	showTimezone = false,
	showToggles = {},
	data = DEFAULT_DATA,
	filters = {},
	onFiltersChange,
	onClear,
	// NEW (sub-epic 9.4) — optional saved-filter slot. Row 1 renders only
	// when `savedFilters` is supplied (empty array is still rendered — lets
	// the user save their first filter).
	savedFilters,
	activeSavedFilterId = null,
	savedFiltersLoading = false,
	onSaveFilter,
	onUpdateFilter,
	onDeleteFilter,
	onSelectSavedFilter,
	onClearActiveSavedFilter,
	orientation = 'horizontal',
	layoutMode,
	onToggleLayout,
	// Desktop-only gate for the compact↔fluid toggle button. Defaults to true
	// so existing callers are unaffected; the Month page passes `desktopSidebar`
	// so the toggle is hidden below 1024.
	showLayoutToggle = true,
	// Optional: render the compact (Drawer) Filters trigger icon-only (funnel
	// icon + aria-label, no "Filters" text). Default false preserves the labeled
	// button for every existing caller; the Month page passes `phone` so the
	// 2-row phone header fits [Filters] + [View] + [Create] in one row
	// (epic 90d77538). Has no effect on the desktop inline / fluid layouts.
	compactTriggerIconOnly = false
}) {
	const d = { ...DEFAULT_DATA, ...data };

	const orgOptions = useMemo(
		() => sortItemsNatural(normalizeOrgOptions(d.organizations), item => item && item.label),
		[d.organizations]
	);
	// `FilterMultiSelect` expects `groups=[{ key, title, items }]`. Org list
	// is flat (no per-org subgroup), so we wrap the whole thing in a single
	// `__ungrouped__` group and disable the parent-toggle UI.
	const orgGroups = useMemo(
		() => [{ key: '__ungrouped__', title: '', items: orgOptions }],
		[orgOptions]
	);
	// Group titles follow the legacy Angular template (`views/calendar/tpl-filter.twig`):
	//  - Location:      `organizationName`
	//  - Resource:      `locationName [organizationName]`
	//  - Pilot:         `locationName [organizationName]` (falls back to org when the bridge
	//                   doesn't expose a location for the pilot)
	//  - Instructor:    `locationName [organizationName]`
	// Keys are composite `${locationID}@${organizationUnique}` where applicable
	// so two orgs that share a location name still get distinct buckets.
	const locationGroups = useMemo(
		() =>
			buildGroups(
				sortItemsNatural(d.locations),
				item => item.organizationUnique,
				item => item.organizationName || 'Other'
			),
		[d.locations]
	);
	const resourceGroups = useMemo(
		() =>
			buildGroups(
				sortItemsNatural(d.resources),
				item => `${item.locationID ?? '-'}@${item.organizationUnique ?? '-'}`,
				item => item.locationName || 'Unknown',
				item => item.organizationName || item.organizationUnique || ''
			),
		[d.resources]
	);
	// Pilots sort by `surname` first then `name` — matches the legacy
	// Angular ordering and keeps families/aliases grouped together.
	const pilotGroups = useMemo(
		() =>
			buildGroups(
				sortItemsNatural(d.pilots, item => `${item?.surname || ''} ${item?.name || ''}`.trim()),
				item => `${item.locationID ?? 'org'}@${item.organizationUnique ?? '-'}`,
				item => item.locationName || item.organizationName || item.organizationUnique || '?',
				item => (item.locationName ? item.organizationName || item.organizationUnique || '' : '')
			),
		[d.pilots]
	);
	const instructorGroups = useMemo(
		() =>
			buildGroups(
				sortItemsNatural(d.instructors, item =>
					`${item?.surname || ''} ${item?.name || ''}`.trim()
				),
				item => `${item.locationID ?? '-'}@${item.organizationUnique ?? '-'}`,
				item => item.locationName || 'Unknown',
				item => item.organizationName || item.organizationUnique || ''
			),
		[d.instructors]
	);

	const emit = partial => onFiltersChange && onFiltersChange(partial);

	// Below desktop (<1024 `navCompact`) the filter block moves into a HeroUI
	// Drawer (triggered by a Filter button) — a real surface instead of a
	// squeezed inline bar / accordion. At >=1024 it renders inline (compact bar
	// or fluid sidebar). The desktop-only layout toggle is already gated out
	// below desktop by `showLayoutToggle` (Phase 1.1), so the Drawer never
	// hosts it.
	const { navCompact } = useBreakpoint();
	const [drawerOpen, setDrawerOpen] = useState(false);

	// Layout toggle. In the fluid VERTICAL sidebar it renders at the BOTTOM
	// (below the toggle-checkboxes), full-width; in the horizontal compact bar it
	// stays in Row 1 (epic REM-TogglePlacement). Single instance only (REM-2
	// de-dup preserved — no header button).
	const showToggleBtn = Boolean(onToggleLayout && showLayoutToggle);
	const renderLayoutToggle = fullWidth => (
		<Tooltip content="Toggle compact / fluid layout">
			<Button
				size="sm"
				variant="bordered"
				className={`border-small border-default-300${fullWidth ? ' w-full min-h-[44px]' : ''}`}
				aria-label={
					layoutMode === 'sidebar' ? 'Switch to compact layout' : 'Switch to fluid layout'
				}
				onPress={onToggleLayout}
				startContent={
					layoutMode === 'sidebar' ? (
						<Minimize2 className="w-4 h-4" />
					) : (
						<Maximize2 className="w-4 h-4" />
					)
				}
			>
				{layoutMode === 'sidebar' ? 'Compact' : 'Fluid'}
			</Button>
		</Tooltip>
	);

	const filterBody = (
		<>
			{/* Row 1 — saved-filters UI. Rendered only when the parent supplies
			   `savedFilters`; otherwise the row collapses (views that don't
			   want the feature simply omit the prop). */}
			{(Array.isArray(savedFilters) || (showToggleBtn && orientation !== 'vertical')) && (
				<div
					className="flex flex-wrap items-center justify-between gap-2"
					data-testid="calendar-filter-bar-row-saved"
				>
					{Array.isArray(savedFilters) ? (
						<SavedFiltersDropdown
							savedFilters={savedFilters}
							activeId={activeSavedFilterId}
							loading={savedFiltersLoading}
							onSelect={onSelectSavedFilter}
							onSave={onSaveFilter}
							onUpdate={onUpdateFilter}
							onDelete={onDeleteFilter}
							onClearActive={onClearActiveSavedFilter}
							// Compact (icon-only Save, no `md:` width upgrades, wrap) in the
							// Drawer (navCompact, <1024) AND the fluid vertical sidebar — the
							// latter is a 300px DESKTOP container where `navCompact` is false,
							// so it needs the container-derived flag too (epic F3 35bff336).
							compact={navCompact || orientation === 'vertical'}
						/>
					) : (
						<span />
					)}
					{/* Horizontal compact bar: toggle stays here. Vertical sidebar moves
					    it to the bottom (after Row 3) — see below. */}
					{showToggleBtn && orientation !== 'vertical' && renderLayoutToggle(false)}
				</div>
			)}

			{/* Row 2 — main selects. `orientation="vertical"` stacks them for the
			   sidebar layout; default horizontal stays the same. */}
			<div
				className={
					orientation === 'vertical'
						? 'flex flex-col items-stretch gap-2 [&>button]:max-w-none [&>button]:w-full'
						: 'flex flex-col md:flex-row md:flex-wrap md:items-end gap-2 [&>button]:w-full [&>button]:max-w-none md:[&>button]:w-auto md:[&>button]:max-w-[220px]'
				}
				data-testid="calendar-filter-bar-row-selects"
			>
				{showTimezone && (
					<FilterTimezoneSelect
						timezones={d.timezones}
						value={filters.timezone || null}
						onChange={value => emit({ timezone: value })}
					/>
				)}

				{fields.includes('org') && (
					<FilterMultiSelect
						label="Organization"
						entityName="organizations"
						groups={orgGroups}
						selected={filters.org || []}
						onChange={next => emit({ org: next })}
						enableGroupToggle={false}
					/>
				)}

				{fields.includes('loc') && (
					<FilterMultiSelect
						label="Location"
						entityName="locations"
						groups={locationGroups}
						selected={filters.location || []}
						onChange={next => emit({ location: next })}
						enableGroupToggle
					/>
				)}

				{fields.includes('res') && (
					<FilterMultiSelect
						label="Resource"
						entityName="resources"
						groups={resourceGroups}
						selected={filters.resource || []}
						onChange={next => emit({ resource: next })}
						enableGroupToggle
					/>
				)}

				{fields.includes('pilot') && (
					<FilterMultiSelect
						label="Pilot"
						entityName="pilots"
						groups={pilotGroups}
						selected={filters.pilot || []}
						onChange={next => emit({ pilot: next })}
						enableGroupToggle
					/>
				)}

				{fields.includes('instructor') && (
					<FilterMultiSelect
						label="Instructor"
						entityName="instructors"
						groups={instructorGroups}
						selected={filters.instructor || []}
						onChange={next => emit({ instructor: next })}
						enableGroupToggle
					/>
				)}

				<Button size="sm" variant="light" onPress={() => onClear && onClear()}>
					Clear
				</Button>
			</div>

			{/* Row 3 — boolean toggles. `compact={navCompact}` switches the Drawer
			   (<1024) to a vertical column of Switch toggles; desktop inline/fluid
			   keep the Checkbox flex-wrap row. */}
			<FilterToggles
				config={showToggles}
				values={{
					maintenance: filters.maintenance,
					myonly: filters.myonly,
					waiting: filters.waiting,
					activityTypeColorScheme: filters.activityTypeColorScheme
				}}
				onChange={partial => emit(partial)}
				compact={navCompact}
			/>

			{/* Fluid vertical sidebar: layout toggle as the LAST, full-width control
			    (below the toggle-checkboxes) — epic REM-TogglePlacement. */}
			{showToggleBtn && orientation === 'vertical' && renderLayoutToggle(true)}
		</>
	);

	// Below desktop: a Filter button opens the filter block in a Drawer.
	if (navCompact) {
		return (
			<div data-testid="calendar-filter-bar">
				{compactTriggerIconOnly ? (
					<Button
						isIconOnly
						variant="bordered"
						size="sm"
						className="min-w-[44px] min-h-[44px] border-small border-default-300"
						aria-label="Filters"
						onPress={() => setDrawerOpen(true)}
					>
						<Filter className="w-4 h-4" aria-hidden="true" />
					</Button>
				) : (
					<Button
						variant="bordered"
						size="sm"
						className="min-h-[44px] border-small border-default-300"
						startContent={<Filter className="w-4 h-4" aria-hidden="true" />}
						onPress={() => setDrawerOpen(true)}
					>
						Filters
					</Button>
				)}
				<Drawer
					isOpen={drawerOpen}
					onOpenChange={setDrawerOpen}
					placement="left"
					size="sm"
				>
					<DrawerContent>
						{onClose => (
							<>
								<DrawerHeader className="flex items-center gap-2 border-b border-default-200">
									<Filter className="w-4 h-4" aria-hidden="true" />
									Filters
								</DrawerHeader>
								<DrawerBody className="py-4">
									<div className="flex flex-col gap-3">{filterBody}</div>
								</DrawerBody>
								{/* Explicit exit (ux-heuristic: emergency exit) — the X/backdrop
								    still work, this is the primary affordance. */}
								<DrawerFooter className="border-t border-default-200">
									<Button
										color="primary"
										className="w-full min-h-[44px]"
										onPress={onClose}
									>
										Apply &amp; Close
									</Button>
								</DrawerFooter>
							</>
						)}
					</DrawerContent>
				</Drawer>
			</div>
		);
	}

	// Desktop (>=1024): inline — compact bar or fluid sidebar (orientation prop).
	return (
		<div
			className="flex flex-col gap-3 rounded-lg bg-default-50 p-3"
			data-testid="calendar-filter-bar"
		>
			{filterBody}
		</div>
	);
}

export default CalendarFilterBar;
export { normalizeOrgOptions, buildGroups, sortItemsNatural };
